import { createStorageSlot } from '@gonfalon/browser-storage/createStorageSlot';

import Logger from './logUtils';

const logger = Logger.get('signupLandingUrlStorage');

/**
 * Preserves the signup landing URL across OAuth flows.
 *
 * When a user lands on /signup?source=agent (or /login, or with other UTM
 * params) the original page URL is otherwise lost during the OAuth redirect.
 * We capture it here so the account-creation POST can include it as
 * `customReferer`, allowing UTM parameters to flow through to the backend
 * analytics pipeline. Google and GitHub both converge on the same signup POST,
 * so this single stash covers both providers.
 *
 * localStorage (rather than per-tab sessionStorage) is used so the value
 * survives the full OAuth round-trip regardless of tab handling, and so it is
 * available no matter where the OAuth flow was initiated (signup page, login
 * page, or Google One-Tap).
 */
export const signupLandingUrlSlot = createStorageSlot('signup-landing-url', {
  storageType: 'localStorage',
});

/**
 * How long a captured landing URL stays valid. Because localStorage outlives
 * the tab, an abandoned signup attempt would otherwise linger and misattribute
 * a later, unrelated signup from the same browser. A short window keeps the
 * value scoped to the current signup intent.
 */
const MAX_AGE_MS = 30 * 60 * 1000;

type StoredLandingUrl = { url: string; ts: number };

function parseStored(raw: string | null): StoredLandingUrl | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<StoredLandingUrl>;
    if (typeof parsed.url === 'string' && typeof parsed.ts === 'number') {
      return { url: parsed.url, ts: parsed.ts };
    }
  } catch {
    // Tolerate a legacy plain-string value from the previous implementation by
    // treating it as freshly captured.
    return { url: raw, ts: Date.now() };
  }
  return null;
}

function isFresh(stored: StoredLandingUrl): boolean {
  return Date.now() - stored.ts < MAX_AGE_MS;
}

/**
 * Whether a URL carries signup attribution (a `source`, `referer`, or any
 * `utm_*` param). Used so a param-less page (e.g. a bare /login) can't shadow a
 * genuine campaign landing URL under first-touch.
 */
function hasAttributionParams(url: string): boolean {
  try {
    const params = new URL(url).searchParams;
    if (params.has('source') || params.has('referer')) {
      return true;
    }
    for (const key of params.keys()) {
      if (key.startsWith('utm_')) {
        return true;
      }
    }
  } catch {
    return false;
  }
  return false;
}

/**
 * Records the signup landing URL. First-touch: a fresh value already in storage
 * is preserved so the original landing page (with its UTM params) wins over
 * later intermediate pages visited during the same signup intent. Exception: a
 * URL that carries attribution params overrides a stored one that has none, so
 * a param-less page (e.g. /login) captured first doesn't shadow a later
 * campaign landing URL.
 */
export function storeSignupLandingUrl(url: string): void {
  const existing = parseStored(signupLandingUrlSlot.get());
  if (existing && isFresh(existing)) {
    if (hasAttributionParams(existing.url) || !hasAttributionParams(url)) {
      return;
    }
  }
  signupLandingUrlSlot.set(JSON.stringify({ url, ts: Date.now() }));
  logger.log(`signup: stored landing URL for signup attribution (${url})`);
}

/**
 * Removes any stored landing URL. Called when a signup completes through a path
 * that supplies its own referer, so a later OAuth-path read can't see a stale
 * value.
 */
export function clearStoredSignupLandingUrl(): void {
  signupLandingUrlSlot.remove();
}

/**
 * If a fresh landing URL was captured, consume and return it. Always clears
 * storage. Returns `null` when there is nothing to apply or the value expired.
 */
export function consumeStoredCustomReferer(): string | null {
  const stored = parseStored(signupLandingUrlSlot.get());
  signupLandingUrlSlot.remove();
  if (stored && isFresh(stored)) {
    logger.log(`signup: consumed stored customReferer for OAuth signup (${stored.url})`);
    return stored.url;
  }
  return null;
}
